import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../Components';
import { formatKWh, CustomStackedBarTooltip, CustomLegend, COLORS } from './ChartUtils';
import { t } from '../i18n';

const DailyEnergyBreakdown = ({ data }) => {
  const [activeData, setActiveData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartRef = useRef(null);

  // Check if metering data is available
  const hasMetering = useMemo(() => {
    if (!data?.energy_stacked_bars) return false;
    const entries = Object.values(data.energy_stacked_bars);
    if (!entries.length) return false;
    // Check if any entry has non-null total_metered
    return entries.some(entry => entry?.total_metered !== null && entry?.total_metered !== undefined);
  }, [data]);

  // Process energy_stacked_bars into chart-ready format
  const processedData = useMemo(() => {
    if (!data?.energy_stacked_bars) return [];

    return Object.entries(data.energy_stacked_bars)
      .map(([date, values]) => ({
        date,
        displayDate: date.slice(5), // MM-DD format for x-axis
        heating: Number(values.heating) || 0,
        consumption: Number(values.consumption) || 0,
        other: hasMetering ? (Number(values.other) || 0) : 0,
        total_metered: hasMetering ? (Number(values.total_metered) || 0) : null,
        total_controlled: Number(values.total_controlled) || 0
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [data, hasMetering]);

  // Calculate summary totals for InfoBox
  const totals = useMemo(() => {
    if (!processedData.length) return null;

    return processedData.reduce((acc, day) => ({
      heating: acc.heating + day.heating,
      consumption: acc.consumption + day.consumption,
      other: acc.other + day.other,
      total_controlled: acc.total_controlled + day.total_controlled,
      total_metered: hasMetering ? acc.total_metered + (day.total_metered || 0) : null
    }), { heating: 0, consumption: 0, other: 0, total_controlled: 0, total_metered: 0 });
  }, [processedData, hasMetering]);

  // Color scheme
  const colorMap = useMemo(() => ({
    heating: '#4285F4',      // Blue
    consumption: '#34A853', // Green
    other: '#FF9800'        // Orange
  }), []);

  // Handle mouse move on chart for legend updates
  const handleMouseMove = (props) => {
    if (props && props.activePayload && props.activePayload.length > 0) {
      const dataPoint = props.activePayload[0].payload;
      setActiveData(dataPoint);
    }
  };

  const handleMouseLeave = () => {
    setActiveData(null);
  };

  // Track container width for responsive legend
  useEffect(() => {
    if (!chartRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(chartRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  if (!processedData.length) return null;

  // Determine which data to show in legend (active or last day)
  const legendData = activeData || processedData[processedData.length - 1];

  // Build legend payload
  const legendPayload = [
    {
      value: t('monthly.energyHeating'),
      dataKey: 'heating',
      color: colorMap.heating
    },
    {
      value: t('monthly.energyConsumption'),
      dataKey: 'consumption',
      color: colorMap.consumption
    }
  ];

  if (hasMetering) {
    legendPayload.push({
      value: t('monthly.energyOther'),
      dataKey: 'other',
      color: colorMap.other
    });
  }

  // Build InfoBox data
  const infoBoxData = [
    { label: t('monthly.energyHeating'), value: formatKWh(totals?.heating, 1) },
    { label: t('monthly.energyConsumption'), value: formatKWh(totals?.consumption, 1) }
  ];

  if (hasMetering) {
    infoBoxData.push({ label: t('monthly.energyOther'), value: formatKWh(totals?.other, 1) });
    infoBoxData.push({ label: t('monthly.totalMetered'), value: formatKWh(totals?.total_metered, 1) });
  }

  infoBoxData.push({ label: t('monthly.totalControlled'), value: formatKWh(totals?.total_controlled, 1) });

  return (
    <div ref={chartRef}>
      {/* InfoBox with summary data */}
      <InfoBox title={t('monthly.energySummary')} dataPoints={infoBoxData} />

      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={processedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="displayDate"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            label={{ value: 'kWh', angle: -90, position: 'insideLeft' }}
            tick={{ fontSize: 11 }}
          />
          <Tooltip content={<CustomStackedBarTooltip formatter={(value) => formatKWh(value, 2)} />} />

          <Bar
            dataKey="heating"
            stackId="energy"
            fill={colorMap.heating}
            name={t('monthly.energyHeating')}
          />
          <Bar
            dataKey="consumption"
            stackId="energy"
            fill={colorMap.consumption}
            name={t('monthly.energyConsumption')}
          />
          {hasMetering && (
            <Bar
              dataKey="other"
              stackId="energy"
              fill={colorMap.other}
              name={t('monthly.energyOther')}
            />
          )}
        </BarChart>
      </ResponsiveContainer>

      {/* Custom Legend */}
      <CustomLegend
        payload={legendPayload}
        colorMap={colorMap}
        activeData={legendData}
        containerWidth={containerWidth}
        valueFormatter={(value) => formatKWh(value, 1)}
      />
    </div>
  );
};

export default DailyEnergyBreakdown;
